import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Upload, Image, Filter } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import AttachmentUploadModal from './AttachmentUploadModal';
import AttachmentGrid from './AttachmentGrid';
import AttachmentViewer from './AttachmentViewer';

/**
 * `bare` drops the card chrome and the uppercase title for a caller that already
 * frames this — a pane with its own header should not contain a second one.
 */
export default function JobCardAttachments({ jobCardId, className = '', bare = false }) {
  const { user } = useAuth();
  const [attachments, setAttachments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [uploadableCategories, setUploadableCategories] = useState([]);
  const [defaultCategoryName, setDefaultCategoryName] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Modals
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [viewerAttachment, setViewerAttachment] = useState(null);
  const [deleteAttachment, setDeleteAttachment] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadAttachments = useCallback(async () => {
    if (!jobCardId) return;
    try {
      const res = await api.get(`/job-cards/${jobCardId}/attachments`);
      setAttachments(res.data.attachments || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [jobCardId]);

  const loadCategories = useCallback(async () => {
    try {
      const [allRes, uploadableRes] = await Promise.all([
        api.get('/attachment-categories'),
        api.get(`/job-cards/${jobCardId}/uploadable-categories`),
      ]);
      setCategories(allRes.data.categories || []);
      setUploadableCategories(uploadableRes.data.categories || []);
      setDefaultCategoryName(uploadableRes.data.default_category_name || '');
    } catch (error) {
    }
  }, [jobCardId]);

  useEffect(() => {
    loadAttachments();
    loadCategories();
  }, [loadAttachments, loadCategories]);

  const filteredAttachments = selectedCategory === 'all'
    ? attachments
    : attachments.filter((a) => a.category_id === selectedCategory);

  // Can user upload?
  const canUpload = uploadableCategories.length > 0;

  // Can user delete this attachment?
  const canDelete = useCallback((attachment) => {
    if (user?.role === 'admin') return true;
    if (attachment.uploaded_by_user_id === user?.id) {
      // Check time window (simplified - backend will enforce)
      return true;
    }
    return false;
  }, [user]);

  const handleDelete = async () => {
    if (!deleteAttachment) return;
    setDeleting(true);
    try {
      await api.delete(`/attachments/${deleteAttachment.id}`);
      toast.success('Attachment deleted');
      setDeleteAttachment(null);
      loadAttachments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete attachment');
    } finally {
      setDeleting(false);
    }
  };

  // Get default category ID
  const defaultCategoryId = uploadableCategories.find(
    (c) => c.name === defaultCategoryName
  )?.id || uploadableCategories[0]?.id;

  const header = (
    <div className="flex items-center justify-between gap-2">
      <span className={`flex min-w-0 items-center gap-2 ${
        bare
          ? 'text-[10px] font-semibold uppercase tracking-wider text-slate-500'
          : 'font-heading text-sm font-semibold uppercase tracking-wider text-slate-600'
      }`}>
        <Image className="h-4 w-4 shrink-0" />
        <span className="truncate">Attachments</span>
        {attachments.length > 0 && (
          <Badge variant="secondary" className="shrink-0">{attachments.length}</Badge>
        )}
      </span>
      {/* shrink-0 so a narrow column shortens the title rather than letting the
          controls ride over it */}
      <span className="flex shrink-0 items-center gap-2">
        {categories.length > 0 && attachments.length > 0 && (
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="h-8 w-40 text-xs">
              <Filter className="mr-1 h-3 w-3" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: cat.color_hex }} />
                    {cat.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {canUpload && (
          <Button
            size="sm"
            onClick={() => setUploadModalOpen(true)}
            className={`bg-brand-blue hover:bg-brand-blue-dark ${bare ? 'h-7 px-2 text-xs' : 'h-8'}`}
          >
            <Upload className="mr-1 h-3.5 w-3.5" />
            Upload
          </Button>
        )}
      </span>
    </div>
  );

  const grid = (
    <AttachmentGrid
      attachments={filteredAttachments}
      categories={categories}
      loading={loading}
      onView={setViewerAttachment}
      onDelete={setDeleteAttachment}
      canDelete={canDelete}
      emptyMessage="No attachments yet. Upload photos or PDFs to document this job card."
    />
  );

  const modals = (
    <>
      {/* Upload Modal */}
      <AttachmentUploadModal
        open={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
        jobCardId={jobCardId}
        categories={uploadableCategories}
        defaultCategoryId={defaultCategoryId}
        onUploadComplete={() => loadAttachments()}
      />

      {/* Viewer */}
      {viewerAttachment && (
        <AttachmentViewer
          attachment={viewerAttachment}
          attachments={filteredAttachments}
          categories={categories}
          onClose={() => setViewerAttachment(null)}
          onDelete={(a) => {
            setViewerAttachment(null);
            setDeleteAttachment(a);
          }}
          canDelete={canDelete(viewerAttachment)}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteAttachment} onOpenChange={() => setDeleteAttachment(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Attachment?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove "{deleteAttachment?.original_filename}" from this job card.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  return bare ? (
    <div className={`space-y-2 ${className}`}>
      {header}
      {grid}
      {modals}
    </div>
  ) : (
    <Card className={`border-slate-100 shadow-card rounded-xl ${className}`}>
      <CardHeader className="pb-3">{header}</CardHeader>
      <CardContent>{grid}</CardContent>
      {modals}
    </Card>
  );
}
